/**
 * scheduleCron.ts
 *
 * Round-trips between the schedule UI options and a 5-field cron expression,
 * and renders a short human label for badges. Only the patterns the UI emits
 * are recognised; anything else is treated as a custom expression.
 */

export type Frequency = 'hourly' | 'daily' | 'weekly' | 'custom'

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

export interface CronParts {
  freq: Frequency
  /** "HH:MM" for daily/weekly. */
  time: string
  /** 0 (Sun) – 6 (Sat) for weekly. */
  weekday: number
  /** Raw expression (always set; the source of truth for 'custom'). */
  expr: string
}

/** Builds a 5-field cron expression from UI selections. */
export function buildCron(opts: { freq: Frequency; time?: string; weekday?: number; custom?: string }): string {
  const { freq, time = '09:00', weekday = 1, custom = '' } = opts
  const [hh, mm] = time.split(':')
  const h = Number(hh)
  const m = Number(mm)
  const min = Number.isFinite(m) ? m : 0
  const hour = Number.isFinite(h) ? h : 9
  switch (freq) {
    case 'hourly': return '0 * * * *'
    case 'daily':  return `${min} ${hour} * * *`
    case 'weekly': return `${min} ${hour} * * ${weekday}`
    case 'custom': return custom.trim()
  }
}

/** Parses a cron expression back into UI selections (best-effort). */
export function parseCron(expr: string): CronParts {
  const fallback: CronParts = { freq: 'custom', time: '09:00', weekday: 1, expr }
  const f = expr.trim().split(/\s+/)
  if (f.length !== 5) return fallback
  const [m, h, dom, mon, dow] = f

  if (expr.trim() === '0 * * * *') return { freq: 'hourly', time: '09:00', weekday: 1, expr }

  const mNum = Number(m)
  const hNum = Number(h)
  const numericTime = Number.isInteger(mNum) && Number.isInteger(hNum) && String(mNum) === m && String(hNum) === h
  if (!numericTime || dom !== '*' || mon !== '*') return fallback
  const time = `${pad2(hNum)}:${pad2(mNum)}`

  if (dow === '*') return { freq: 'daily', time, weekday: 1, expr }

  const dowNum = Number(dow)
  if (Number.isInteger(dowNum) && dowNum >= 0 && dowNum <= 6 && String(dowNum) === dow) {
    return { freq: 'weekly', time, weekday: dowNum, expr }
  }
  return fallback
}

/** Short human label for a cron expression, for badges. */
export function cronLabel(expr: string): string {
  const p = parseCron(expr)
  switch (p.freq) {
    case 'hourly': return 'Hourly'
    case 'daily':  return `Daily ${p.time}`
    case 'weekly': return `Weekly ${WEEKDAYS[p.weekday]} ${p.time}`
    case 'custom': return 'Custom'
  }
}
